import onetime from "@-/com.npmjs.onetime";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import type {
  AnalyticsEventName,
  AnalyticsEventProperties,
  AnalyticsProperties,
  CapturedAnalyticsEvent,
} from "#analytics-event";
import { getAnalyticsContext } from "#analytics-context";
import { notifyAnalyticsEventListeners } from "#analytics-event-listeners";
import { deliverToAnalyticsSink } from "#analytics-sink";

/** Retried writes reuse their operation id; this many recent keys are remembered. */
const DEDUPE_KEY_LIMIT = 128;

const getRecentDedupeKeys = onetime((): Set<string> => new Set());

/** True the first time `key` is seen among the recent keys. */
function claimDedupeKey(key: string): boolean {
  const keys = getRecentDedupeKeys();
  if (keys.has(key)) {
    return false;
  }
  keys.add(key);
  if (keys.size > DEDUPE_KEY_LIMIT) {
    const oldest = keys.values().next().value;
    if (oldest !== undefined) {
      keys.delete(oldest);
    }
  }
  return true;
}

/**
 * Captures one product event. Synchronous and never throws: callers capture after their write has
 * committed, and analytics must not be able to fail (or slow) the action that triggered it.
 *
 * `dedupeKey` drops a repeat with the same key (e.g. a retried card action's operation id), and
 * `timestamp` backdates an event recovered after the fact.
 */
export function captureAnalyticsEvent<Name extends AnalyticsEventName>(
  name: Name,
  properties: AnalyticsEventProperties[Name],
  options: { dedupeKey?: string; timestamp?: Date } = {},
): void {
  try {
    if (options.dedupeKey !== undefined && !claimDedupeKey(`${name}:${options.dedupeKey}`)) {
      return;
    }
    const event: CapturedAnalyticsEvent = {
      name,
      properties: { ...getAnalyticsContext(), ...(properties as AnalyticsProperties) },
      timestamp: options.timestamp ?? new Date(),
    };
    notifyAnalyticsEventListeners(event);
    deliverToAnalyticsSink(event);
  } catch (error) {
    logger(import.meta.location).warn("Analytics event capture failed", { error, name });
  }
}
